import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  effect,
  inject,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserService } from 'libs/core/src/lib/services/user.service';
import { SkeletonTemplateService } from '@skeleton-loader';
import { CheckoutAddress } from '@models';
import { AddressFormComponent } from '../address-form/address-form.component';
import { CapitalFirstLetterPipe } from '../pipes';
import { createAddressForm, patchAddressForm, toCheckoutAddress } from '../address-form/address-form.util';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { animate, style, transition, trigger } from '@angular/animations';
import { SnackbarService } from '../snackbar/snackbar.service';

@Component({
  selector: 'lib-checkout-shipping-information',
  templateUrl: './checkout-shipping-information.component.html',
  styleUrls: ['./checkout-shipping-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatTooltipModule,
    AddressFormComponent,
    CapitalFirstLetterPipe,
    FormsModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
  animations: [
    trigger('alertAnimation', [
      transition(':enter', [
        style({ maxHeight: '0', overflow: 'hidden', marginBottom: '0', opacity: 0 }),
        animate(
          '400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          style({ maxHeight: '200px', overflow: 'hidden', marginBottom: '24px', opacity: 1 }),
        ),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden', opacity: 1 }),
        animate(
          '150ms cubic-bezier(0.55, 0.055, 0.675, 0.19)',
          style({ maxHeight: '0', marginBottom: '0', opacity: 0 }),
        ),
      ]),
    ]),
  ],
})
export class CheckoutShippingInformationComponent {
  addressTooltip =
    'Shipments cannot be delivered to PO Box addresses, United States territories, or addresses outside of the United States.';

  readonly submitAction = output<CheckoutAddress>();
  readonly emailChange = output<string>();

  readonly skeletonService = inject(SkeletonTemplateService);
  private readonly userService = inject(UserService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly snackbarService = inject(SnackbarService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly emailErrorMessage = 'Please enter a valid email before continuing.';

  email = this.userService.emailAddress;
  isUserLoaded = this.userService.isUserLoaded;
  // Shown as received from the casino (empty stays empty); the order API does not take a name.
  firstName = this.userService.firstName;
  lastName = this.userService.lastName;

  // Shipping address is entered here; it is prefilled once from the casino home address.
  readonly addressForm = createAddressForm();
  private isAddressPrefilled = false;

  isEditEmail = signal(false);
  emailValue = linkedSignal(() => this.email() ?? '');
  emailNotificationShown = signal(false);

  readonly isEmailValid = computed(() => {
    const value = this.emailValue();
    return !!value && !Validators.email(new FormControl(value));
  });

  readonly isContinueDisabled = computed(() => this.isEditEmail() || !this.isEmailValid());

  /**
   * RID-1355 — the id of this component's own e-mail banner. The top banners are a stack now, so
   * dismissing has to be scoped: a bare `dismiss()` here used to wipe every other banner on the
   * Checkout page (the "Cart updated" one among them).
   */
  private emailBannerId?: number;

  constructor() {
    effect(() => {
      if (!this.isUserLoaded() || this.isAddressPrefilled || this.addressForm.dirty) return;
      this.isAddressPrefilled = true;
      patchAddressForm(this.addressForm, this.userService.address() ?? {});
    });

    effect(() => {
      // While the user request is in flight the e-mail is just not known yet, not invalid.
      if (!this.isUserLoaded() || this.isEmailValid()) {
        this.dismissEmailBanner();
      } else {
        this.emailBannerId = this.snackbarService.error('', this.emailErrorMessage, { sticky: true });
      }
    });

    this.destroyRef.onDestroy(() => this.dismissEmailBanner());
  }

  private dismissEmailBanner(): void {
    if (this.emailBannerId === undefined) return;
    this.snackbarService.dismiss(this.emailBannerId);
    this.emailBannerId = undefined;
  }

  editEmail(): void {
    this.isEditEmail.set(true);
  }

  saveEmail(): void {
    this.isEditEmail.set(false);
    this.emailNotificationShown.set(true);
    this.emailChange.emit(this.emailValue());
  }

  onContinue(): void {
    if (this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      this.focusFirstInvalidField();
      return;
    }
    this.submitAction.emit(toCheckoutAddress(this.addressForm));
  }

  private focusFirstInvalidField(): void {
    setTimeout(() => {
      const field = this.elementRef.nativeElement.querySelector<HTMLElement>(
        'lib-address-form input.ng-invalid, lib-address-form mat-select.ng-invalid',
      );
      field?.focus();
    });
  }

  cancelEmail(): void {
    this.emailValue.set(this.email() ?? '');
    this.isEditEmail.set(false);
  }

  closeEmailNotification(): void {
    this.emailNotificationShown.set(false);
  }
}
